import {
  Button,
  Group,
  Code,
  ScrollArea,
  Table,
  Text,
  Collapse,
  ActionIcon,
  Stack,
} from '@mantine/core';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState } from 'react';

const NestedObjectFieldsTable = ({ obj }: { obj: object }) => {
  const [expandedKeyPaths, setExpandedKeyPaths] = useState<string[]>([]);

  const handleToggleExpand = (keyPath: string) => {
    setExpandedKeyPaths((prev) =>
      prev.includes(keyPath)
        ? prev.filter((path) => path !== keyPath)
        : [...prev, keyPath],
    );
  };

  const isValueObject = (value: unknown): value is object => {
    return (
      typeof value === 'object' && value !== null && !(value instanceof Date)
    );
  };

  const canExpandValue = (value: unknown): boolean => {
    return isValueObject(value) && Object.keys(value).length > 0;
  };

  return (
    <ScrollArea>
      <Table variant='vertical'>
        <Table.Tbody>
          {Object.entries(obj)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([key, value]) => (
              <Table.Tr key={key}>
                <Table.Th style={{ verticalAlign: 'top' }}>
                  <Group gap='xs' align='flex-start' h='100%' wrap='nowrap'>
                    <Text size='xs' fw={600}>
                      {key}
                    </Text>
                    {canExpandValue(value) && (
                      <ActionIcon
                        onClick={() => handleToggleExpand(key)}
                        size='xs'
                        variant='subtle'
                      >
                        {expandedKeyPaths.includes(key) ? (
                          <ChevronDownIcon size={16} />
                        ) : (
                          <ChevronRightIcon size={16} />
                        )}
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Th>
                <Table.Td>
                  {isValueObject(value) ? (
                    <ObjectValue
                      value={value}
                      key={key}
                      isExpanded={expandedKeyPaths.includes(key)}
                      onToggleExpand={() => handleToggleExpand(key)}
                    />
                  ) : (
                    <Text size='xs'>
                      {value === undefined
                        ? 'undefined'
                        : value === null
                          ? 'null'
                          : value.toString()}
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
};

export default NestedObjectFieldsTable;

const ObjectValue = ({
  value,
  isExpanded,
  onToggleExpand,
}: {
  value: object;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) => {
  return (
    <Group gap='0'>
      {!isExpanded && (
        <Text size='xs'>{`Object (${Object.keys(value).length} keys)`}</Text>
      )}
      <Collapse in={isExpanded}>
        <NestedObjectFieldsTable obj={value} />
      </Collapse>
    </Group>
  );
};
